import axios, { AxiosInstance, AxiosError } from 'axios'
import { useAuthStore } from '../store/authStore'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'

let api: AxiosInstance

export function initializeApi() {
  api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor to attach token
  api.interceptors.request.use((config) => {
    const { accessToken, user } = useAuthStore.getState()

    // Viewer accounts are read-only. The backend rejects every write with 403
    // anyway; this fails fast with a clear message instead of a server round-trip.
    // (Logout / token refresh are allowed so a viewer can still sign out.)
    const method = (config.method || 'get').toLowerCase()
    const url = config.url || ''
    if (
      user?.role === 'viewer' &&
      !['get', 'head', 'options'].includes(method) &&
      !/\/auth\/(logout|token\/refresh)/.test(url)
    ) {
      return Promise.reject({
        message: 'This is a view-only account.',
        config,
        response: { status: 403, data: { detail: 'This is a view-only account. You do not have permission to make changes.' } },
      })
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  })

  // Response interceptor to handle token refresh
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any
      const { refreshToken, setTokens, clearAuth } = useAuthStore.getState()

      if (error.response?.status === 401 && refreshToken && !originalRequest._retry) {
        originalRequest._retry = true
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
            refresh: refreshToken,
          })
          const { access, refresh } = response.data
          setTokens(access, refresh)
          originalRequest.headers.Authorization = `Bearer ${access}`
          return api(originalRequest)
        } catch (refreshError) {
          clearAuth()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      }
      return Promise.reject(error)
    }
  )

  return api
}

export function getApi() {
  if (!api) {
    initializeApi()
  }
  return api
}

// Auth endpoints
export const authAPI = {
  login: (email: string, password: string) =>
    getApi().post('/auth/login/', { email, password }),
  
  logout: () =>
    getApi().post('/auth/logout/', {}),
  
  refreshToken: (refresh: string) =>
    getApi().post('/auth/refresh/', { refresh }),

  getCurrentUser: () =>
    getApi().get('/auth/me/'),
}

// Dashboard endpoints
export const dashboardAPI = {
  getSummary: (filters = {}) =>
    getApi().get('/dashboard/summary/', { params: filters }),

  getSystemStatus: () =>
    getApi().get('/dashboard/system-status/'),

  getEmployeeStats: () =>
    getApi().get('/dashboard/employee-stats/'),

  // status: at_work | on_leave | absent | checked_in_outside | checked_out_outside
  // `config` lets callers pass an AbortSignal ({ signal }) to cancel in-flight requests.
  getEmployeesByStatus: (status: string, page = 1, search = '', config: any = {}) =>
    getApi().get('/dashboard/employees-by-status/', { params: { status, page, search }, ...config }),
}

// Employees endpoints
export const employeesAPI = {
  list: (page = 1, search = '') =>
    getApi().get('/employees/', { params: { page, search } }),
  
  create: (data: any) =>
    getApi().post('/employees/', data),
  
  update: (id: string, data: any) =>
    getApi().patch(`/employees/${id}/`, data),
  
  deactivate: (id: string) =>
    getApi().post(`/employees/${id}/deactivate/`, {}),

  activate: (id: string) =>
    getApi().post(`/employees/${id}/activate/`, {}),

  downloadTemplate: () =>
    getApi().get('/employees/template/', { responseType: 'blob' }),

  uploadPhoto: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('photo', file)
    return getApi().post(`/employees/${id}/photo/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  resetPassword: (id: string, password?: string) =>
    getApi().post(`/employees/${id}/reset-password/`, password ? { password } : {}),
  
  bulkUpload: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return getApi().post('/employees/bulk-upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  
  getProfile: (id: string) =>
    getApi().get(`/employees/${id}/`),  
}

// Attendance endpoints
// Backend expects multipart/form-data with flat fields: lat, lng, photo (file), address
function buildAttendanceFormData(location: any, photo: string | File) {
  const formData = new FormData()
  formData.append('lat', String(location.latitude))
  formData.append('lng', String(location.longitude))
  if (location.address) formData.append('address', location.address)

  if (typeof photo === 'string') {
    // photo is a base64 data URL from CameraCapture - convert to a file
    const arr = photo.split(',')
    const mimeMatch = arr[0].match(/:(.*?);/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    formData.append('photo', new Blob([u8arr], { type: mime }), 'photo.jpg')
  } else {
    formData.append('photo', photo)
  }
  return formData
}

export const attendanceAPI = {
  checkIn: (location: any, photo: string | File) =>
    getApi().post('/attendance/check-in/', buildAttendanceFormData(location, photo), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  checkOut: (location: any, photo: string | File) =>
    getApi().post('/attendance/check-out/', buildAttendanceFormData(location, photo), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  // Admin-controlled geofence policy (GET: any user; PATCH: admin only)
  getGeofencePolicy: () => getApi().get('/attendance/geofence-policy/'),
  updateGeofencePolicy: (data: {
    mode?: 'MONITOR' | 'ENFORCE'
    enforce_from?: string | null
    require_training_centre?: boolean
  }) => getApi().patch('/attendance/geofence-policy/', data),

  // History of the automatic nightly finalization
  getFinalizationRuns: () => getApi().get('/attendance/finalization-runs/'),

  getHistory: (page = 1, filters: any = {}) =>
    getApi().get('/attendance/mine/', { params: { page, ...filters } }),
  
  getTodayStatus: () =>
    getApi().get('/attendance/today/'),
  
  getAllAttendance: (filters = {}) =>
    getApi().get('/attendance/', { params: filters }),

  adminEdit: (id: string, data: any) =>
    getApi().patch(`/attendance/${id}/admin-edit/`, data),

  getShiftConfig: () =>
    getApi().get('/attendance/shift-config/'),

  updateShiftConfig: (data: any) =>
    getApi().patch('/attendance/shift-config/', data),
  
  export: (filters: any = {}) =>
    getApi().get('/attendance/export/', { params: filters, responseType: 'blob' }),
}

// End-of-Day Report endpoints
export const reportAPI = {
  submit: (data: any) =>
    getApi().post('/daily-reports/', data),
  
  getToday: () =>
    getApi().get('/daily-reports/today/'),
  
  getHistory: (page = 1) =>
    getApi().get('/daily-reports/mine/', { params: { page } }),
  
  getAllReports: (filters = {}) =>
    getApi().get('/daily-reports/', { params: filters }),
}

// Leave endpoints
// Backend's GET /leave/ returns the caller's own requests for employees and
// all requests for admins (role-based), so getMyRequests and getAllRequests
// both hit the same URL.
export const leaveAPI = {
  submitRequest: (data: any) =>
    getApi().post('/leave/', data),
  
  getMyRequests: (page = 1) =>
    getApi().get('/leave/', { params: { page } }),
  
  getAllRequests: (filters = {}) =>
    getApi().get('/leave/', { params: filters }),
  
  approveRequest: (id: string, admin_comment = '') =>
    getApi().post(`/leave/${id}/approve/`, { admin_comment }),
  
  rejectRequest: (id: string, admin_comment: string) =>
    getApi().post(`/leave/${id}/reject/`, { admin_comment }),
}

// Audit log endpoints (admin only, backed by real DB records)
export const auditAPI = {
  list: (filters: any = {}) =>
    getApi().get('/audit-logs/', { params: filters }),
}

// Districts endpoints
export const districtsAPI = {
  list: (page = 1) =>
    getApi().get('/districts/', { params: { page } }),

  listAll: () =>
    getApi().get('/districts/all/'),

  create: (data: any) =>
    getApi().post('/districts/', data),

  update: (id: string, data: any) =>
    getApi().patch(`/districts/${id}/`, data),

  delete: (id: string) =>
    getApi().delete(`/districts/${id}/`),
}

// Training Centers endpoints
export const centersAPI = {
  list: (page = 1, filters: any = {}) =>
    getApi().get('/training-centres/', { params: { page, ...filters } }),

  listAll: (filters: any = {}) =>
    getApi().get('/training-centres/all/', { params: filters }),
  
  create: (data: any) =>
    getApi().post('/training-centres/', data),
  
  update: (id: string, data: any) =>
    getApi().patch(`/training-centres/${id}/`, data),
  
  delete: (id: string) =>
    getApi().delete(`/training-centres/${id}/`),
}
// Holiday configuration endpoints
export const holidaysAPI = {
  list: (page = 1, filters: any = {}) =>
    getApi().get('/holidays/', { params: { page, ...filters } }),

  create: (data: any) =>
    getApi().post('/holidays/', data),

  update: (id: string, data: any) =>
    getApi().patch(`/holidays/${id}/`, data),

  delete: (id: string) =>
    getApi().delete(`/holidays/${id}/`),

  downloadTemplate: () =>
    getApi().get('/holidays/template/', { responseType: 'blob' }),

  bulkUpload: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return getApi().post('/holidays/bulk-upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Employee-facing — no pagination params needed, both endpoints return
  // the employee's own scoped list in full.
  getUpcoming: () =>
    getApi().get('/holidays/upcoming/'),

  getCalendar: (year: number) =>
    getApi().get('/holidays/calendar/', { params: { year } }),
}

// Weekly-off configuration endpoints
export const weekOffAPI = {
  list: (filters: any = {}) =>
    getApi().get('/holidays/week-off-rules/', { params: filters }),

  // Replaces ALL active rules for one scope target in a single atomic
  // call — this is the only write path (see backend SetScopeWeekOffView
  // docstring for why there's no separate create/delete per rule).
  setScope: (payload: {
    scope: 'GLOBAL' | 'EMPLOYEE' | 'TRAINING_CENTRE' | 'DISTRICT'
    employee?: number | string
    training_centre?: number | string
    district?: number | string
    rules: { day_of_week: number; week_pattern: string }[]
  }) => getApi().post('/holidays/week-off-rules/set/', payload),

  getEffective: (employeeId: number | string) =>
    getApi().get('/holidays/week-off-rules/effective/', { params: { employee: employeeId } }),
}

export default getApi
